(function(){
'use strict';

scProgressDataFactory.$inject = ["scCurrencyService", "scSearchProgressService", "$q", "$location"];
function scProgressDataFactory(scCurrencyService, scSearchProgressService, $q, $location) {
  // Helper function to pull out commitment details. Depending on where the data comes from (graphql vs api), the
  // commitment details may be flattened or under a commitment object.
  function getEntityCommitment(entity) {
    if (!entity.commitment && !entity.commitment_deadline) {
      return null;
    }
    if (entity.commitment) {
      return entity.commitment;
    }
    return {
      value: entity.commitment_value,
      deadline: entity.commitment_deadline
    };
  }

  // All entities that aren't tribute pages should have a raw_goal (m11y or not) that is in the same
  // currency as their raw_currency_code
  function getEntityProgress(entity, explicitType) {
    // Try to get fresh percent_to_goal from Search if entity has an ID
    if (entity && entity.id) {
      var entityType = void 0;

      // Priority 1: If caller explicitly provided the type, use that (most reliable)
      if (explicitType === 'team' || explicitType === 'parentTeam') {
        entityType = 'team';
      } else if (explicitType === 'page' || explicitType === 'fundraiser') {
        entityType = 'page';
      }

      // Priority 2: If no explicit type provided, check the entity data itself
      if (!entityType) {
        // team_lead_id is unique to teams - if present, it's definitely a team
        if (entity.team_lead_id) {
          entityType = 'team';
        } else if (entity.fundraising_team_id !== undefined) {
          // Has fundraising_team_id field (even if null/0) = definitely a fundraising page
          // Note: fundraising_team_id can be null/0 for pages without a parent team
          // We can't use member_id because both teams and pages have member_id
          entityType = 'page';
        }
      }

      // Priority 3: If we couldn't determine type from entity data, try URL-based detection
      if (!entityType) {
        var url = $location.absUrl();

        if (url.indexOf('/team/') > -1 || url.indexOf('ftid=') > -1) {
          entityType = 'team';
        } else if (url.indexOf('/fundraiser/') > -1 || url.indexOf('fcid=') > -1) {
          entityType = 'page';
        } else if (url.indexOf('/give/') > -1) {
          // For /give/ URLs, check the ID prefix (t = team, f = fundraiser, otherwise campaign)
          var location = document.location.href;
          var id = location.match('/give/(.*)/#');
          if (id && id[1]) {
            if (id[1][0] === 't') {
              entityType = 'team';
            } else if (id[1][0] === 'f') {
              entityType = 'page';
            }
            // If it's a campaign (no prefix or other prefix), entityType remains undefined
            // and we'll fall through to sync fallback
          }
        }
      }

      // Priority 4: If we still couldn't determine type, use sync version for safety
      if (!entityType) {
        // Entity type unclear or incomplete data - use sync version for safety
        // This includes entities from donation pages, incomplete API responses, etc.
        return $q.resolve(getEntityProgressSync(entity));
      }

      return scSearchProgressService.getEntityProgress(entityType, entity.id).then(function (searchData) {
        // Use Search data for percent_to_goal if available, otherwise fall back to entity data
        var percentToGoal = searchData ? searchData.percent_to_goal : _.get(entity, 'percent_to_goal', 0);

        // APIv2 returns 'raw_goal', but ClassyQL returns 'goal_raw'. Account for both
        var rawGoalString = _.get(entity, 'raw_goal', _.get(entity, 'goal_raw', '0'));
        var rawGoal = parseInt(rawGoalString, 10);

        // for pages without goals on campaigns with donor covered fees (DCF), we can use the progress bar
        // amount to display the amount raised in the raw currency the fundraising page was created with
        var progressBarAmount = searchData ? searchData.progress_bar_amount : _.get(entity, 'progress_bar_amount', null);

        // if a raw goal isn't set (aka 0), we need to use normalized attributes for fundraising pages
        // Use Search total_raised if available, otherwise fall back to entity data
        var totalRaisedNormalized = searchData ? searchData.total_raised : _.get(entity, 'total_raised', 0);

        // normalized_currency_code exists on fundraising pages that come from search. If we only have data from APIv2, then currency_code represents the normalized currency code.
        var currencyCodeNormalized = _.get(entity, 'normalized_currency_code', _.get(entity, 'currency_code', 'USD'));

        var totalRaised = void 0;
        var currency = void 0;
        if (rawGoal) {
          totalRaised = percentToGoal * rawGoal / 100;
          currency = scCurrencyService.getEntityCurrency(entity);
        } else if (progressBarAmount) {
          totalRaised = progressBarAmount;
          currency = scCurrencyService.getEntityCurrency(entity);
        } else {
          totalRaised = totalRaisedNormalized;
          currency = currencyCodeNormalized;
        }

        return {
          percentToGoal: percentToGoal / 100,
          totalRaised: totalRaised,
          goal: rawGoal,
          currency: currency,
          commitment: getEntityCommitment(entity)
        };
      }).catch(function () {
        return (
          // If Search fails, fall back to original logic
          $q.resolve(getEntityProgressSync(entity))
        );
      });
    }

    // If no ID or Search service not available, use synchronous version
    return $q.resolve(getEntityProgressSync(entity));
  }

  // Synchronous version of getEntityProgress for fallback
  function getEntityProgressSync(entity) {
    var percentToGoal = _.get(entity, 'percent_to_goal', 0);

    // APIv2 returns 'raw_goal', but ClassyQL returns 'goal_raw'. Account for both
    var rawGoalString = _.get(entity, 'raw_goal', _.get(entity, 'goal_raw', '0'));
    var rawGoal = parseInt(rawGoalString, 10);

    // for pages without goals on campaigns with donor covered fees (DCF), we can use the progress bar
    // amount to display the amount raised in the raw currency the fundraising page was created with
    var progressBarAmount = _.get(entity, 'progress_bar_amount', null);

    // if a raw goal isn't set (aka 0), we need to use normalized attributes for fundraising pages
    var totalRaisedNormalized = _.get(entity, 'total_raised', 0);

    // normalized_currency_code exists on fundraising pages that come from search. If we only have data from APIv2, then currency_code represents the normalized currency code.
    var currencyCodeNormalized = _.get(entity, 'normalized_currency_code', _.get(entity, 'currency_code', 'USD'));

    var totalRaised = void 0;
    var currency = void 0;
    if (rawGoal) {
      totalRaised = percentToGoal * rawGoal / 100;
      currency = scCurrencyService.getEntityCurrency(entity);
    } else if (progressBarAmount) {
      totalRaised = progressBarAmount;
      currency = scCurrencyService.getEntityCurrency(entity);
    } else {
      totalRaised = totalRaisedNormalized;
      currency = currencyCodeNormalized;
    }

    return {
      percentToGoal: percentToGoal / 100,
      totalRaised: totalRaised,
      goal: rawGoal,
      currency: currency,
      commitment: getEntityCommitment(entity)
    };
  }

  function getCampaignProgress(campaign) {
    // Try to get fresh percent_to_goal from Search if campaign has an ID
    if (campaign && campaign.id) {
      return scSearchProgressService.getCampaignProgress(campaign.id).then(function (searchData) {
        var _campaign$raw_goal = campaign.raw_goal,
            goalString = _campaign$raw_goal === undefined ? '0' : _campaign$raw_goal;

        var goal = parseInt(goalString, 10);

        // Use Search data for percent_to_goal if available, otherwise fall back to campaign.overview
        var percentToGoal = searchData ? searchData.percent_to_goal : _.get(campaign, 'overview.percent_to_goal', 0);

        return {
          percentToGoal: (percentToGoal || 0) / 100,
          totalRaised: percentToGoal * goal / 100,
          goal: goal,
          currency: scCurrencyService.getEntityCurrency(campaign)
        };
      }).catch(function () {
        return (
          // If Search fails, fall back to original logic
          $q.resolve(getCampaignProgressSync(campaign))
        );
      });
    }

    // If no ID or Search service not available, use synchronous version
    return $q.resolve(getCampaignProgressSync(campaign));
  }

  // Synchronous version of getCampaignProgress for fallback
  function getCampaignProgressSync(campaign) {
    var _campaign$raw_goal2 = campaign.raw_goal,
        goalString = _campaign$raw_goal2 === undefined ? '0' : _campaign$raw_goal2;

    var goal = parseInt(goalString, 10);

    var _ref = campaign.overview || {},
        percentToGoal = _ref.percent_to_goal;

    return {
      percentToGoal: (percentToGoal || 0) / 100,
      totalRaised: percentToGoal * goal / 100,
      goal: goal,
      currency: scCurrencyService.getEntityCurrency(campaign)
    };
  }

  return { getEntityProgress: getEntityProgress, getCampaignProgress: getCampaignProgress, getEntityProgressSync: getEntityProgressSync, getCampaignProgressSync: getCampaignProgressSync };
}

angular.module('classy').factory('scProgressDataFactory', scProgressDataFactory);
})();